var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback } from "react";
import useStore from "../Store/store";
/**
 * Generates a request ID with safe fallbacks for environments
 * where crypto.randomUUID() is not available.
 *
 * Fallback chain:
 * 1. crypto.randomUUID() - preferred, available in modern browsers
 * 2. crypto.getRandomValues() - UUID generation fallback
 * 3. Date.now() + Math.random() - basic fallback
 */
function generateRequestId() {
    try {
        // Try the standard crypto.randomUUID() first
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    }
    catch (_a) {
        // Silently fall through to next method
    }
    try {
        // Fallback: use crypto.getRandomValues() to generate UUID
        if (typeof crypto !== "undefined" && crypto.getRandomValues) {
            var arr = new Uint8Array(16);
            crypto.getRandomValues(arr);
            arr[6] = (arr[6] & 0x0f) | 0x40; // version 4
            arr[8] = (arr[8] & 0x3f) | 0x80; // variant 1
            var hex = Array.from(arr, function (byte) { return byte.toString(16).padStart(2, "0"); }).join("");
            return "".concat(hex.slice(0, 8), "-").concat(hex.slice(8, 12), "-").concat(hex.slice(12, 16), "-").concat(hex.slice(16, 20), "-").concat(hex.slice(20));
        }
    }
    catch (_b) {
        // Silently fall through to final fallback
    }
    // Final fallback: timestamp + random number
    return "".concat(Date.now(), "-").concat(Math.random().toString(36).substring(2, 11));
}
/**
 * Returns a stable `run` function that wraps any apiCore call with:
 * - setActionStart  → status: "loading"
 * - setActionSuccess → status: "success"
 * - setActionError  → status: "error"
 *
 * Usage:
 *   const run = useApiAction(ACTION_KEYS.FETCH_DATA);
 *   try {
 *     const { data, ok } = await run(() => apiCore.fetchData(...args));
 *   } catch (err) {
 *     // handle error — run re-throws on failure
 *   }
 */
export function useApiAction(actionKey) {
    var _this = this;
    var setActionStart = useStore(function (s) { return s.setActionStart; });
    var setActionSuccess = useStore(function (s) { return s.setActionSuccess; });
    var setActionError = useStore(function (s) { return s.setActionError; });
    var run = useCallback(function (fn) { return __awaiter(_this, void 0, void 0, function () {
        var requestId, data, err_1, message, code;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    requestId = generateRequestId();
                    setActionStart(actionKey, requestId);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fn()];
                case 2:
                    data = _a.sent();
                    setActionSuccess(actionKey, requestId);
                    return [2 /*return*/, { data: data, error: null, ok: true }];
                case 3:
                    err_1 = _a.sent();
                    message = err_1 instanceof Error
                        ? err_1.message
                        : typeof err_1 === "object" && err_1 !== null && "message" in err_1
                            ? String(err_1.message)
                            : "Unknown error";
                    code = typeof err_1 === "object" && err_1 !== null && "code" in err_1
                        ? err_1.code
                        : undefined;
                    // Use the locally-generated requestId to correlate this error with the
                    // specific action attempt, ensuring consistent tracking across the UI/logs.
                    setActionError(actionKey, message, code, requestId);
                    // Re-throw the error to trigger catch blocks at the call site,
                    // preventing the execution of subsequent 'then' logic.
                    throw err_1;
                case 4: return [2 /*return*/];
            }
        });
    }); }, [actionKey, setActionStart, setActionSuccess, setActionError]);
    return run;
}
